import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Bell, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGetNotifications } from '@/hooks/queries/use-get-notifications';
import { useNotificationCenterStore } from '@/stores/notification-center-store';
import { NotificationItem } from '@/types/notification-types';
import { useOpenNotification } from './use-open-notification';
import { getNotificationTarget } from './helpers/get-notification-target';

const MAX_VISIBLE = 3;
const TOAST_DURATION_MS = 8000;

export const NotificationToaster: FC = () => {
  const { data } = useGetNotifications();
  const isOpen = useNotificationCenterStore((s) => s.isOpen);
  const openNotification = useOpenNotification();

  const seenIds = useRef<Set<string> | null>(null);
  const [toasts, setToasts] = useState<NotificationItem[]>([]);

  const items = data?.items;

  useEffect(() => {
    if (!items) return;
    if (seenIds.current === null) {
      seenIds.current = new Set(items.map((i) => i.id));
      return;
    }
    const fresh = items.filter(
      (i) => !seenIds.current?.has(i.id) && !i.read_at,
    );
    if (fresh.length === 0) return;
    fresh.forEach((i) => seenIds.current?.add(i.id));
    if (isOpen) return;
    setToasts((prev) => [...fresh, ...prev].slice(0, MAX_VISIBLE));
  }, [items, isOpen]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleOpen = useCallback(
    (notification: NotificationItem) => {
      openNotification(notification);
      dismiss(notification.id);
    },
    [openNotification, dismiss],
  );

  return createPortal(
    <ToastPrimitive.Provider duration={TOAST_DURATION_MS} swipeDirection='left'>
      <AnimatePresence>
        {toasts.map((notification) => {
          const hasTarget = Boolean(getNotificationTarget(notification));
          return (
            <ToastPrimitive.Root
              key={notification.id}
              asChild
              forceMount
              duration={TOAST_DURATION_MS}
              onOpenChange={(open) => {
                if (!open) dismiss(notification.id);
              }}
            >
              <motion.div
                initial={{ x: -120, opacity: 0 }}
                animate={{ x: 0, opacity: 1, transition: { duration: 0.25 } }}
                exit={{ x: -60, opacity: 0, transition: { duration: 0.15 } }}
                layout
                className='relative w-full rounded-lg border bg-background shadow-lg'
              >
                <button
                  type='button'
                  onClick={() => handleOpen(notification)}
                  disabled={!hasTarget}
                  className={cn(
                    'flex w-full items-start gap-3 p-4 text-left',
                    hasTarget && 'cursor-pointer',
                  )}
                >
                  <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-accent'>
                    <Bell className='size-4 text-foreground' />
                  </span>
                  <span className='flex min-w-0 flex-1 flex-col gap-1 pr-4'>
                    <span className='truncate text-sm font-medium text-foreground'>
                      {notification.title}
                    </span>
                    {notification.body && (
                      <span className='line-clamp-2 text-sm text-muted-foreground'>
                        {notification.body}
                      </span>
                    )}
                    {hasTarget && (
                      <span className='mt-1 inline-flex items-center gap-1 text-sm text-primary'>
                        Open
                        <ArrowUpRight className='size-3.5' />
                      </span>
                    )}
                  </span>
                </button>
                <ToastPrimitive.Close
                  aria-label='Dismiss'
                  className='absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/10'
                >
                  <X className='size-4' />
                </ToastPrimitive.Close>
              </motion.div>
            </ToastPrimitive.Root>
          );
        })}
      </AnimatePresence>
      <ToastPrimitive.Viewport className='fixed bottom-0 left-0 z-60 flex w-[390px] max-w-[calc(100vw-2rem)] flex-col gap-3 p-6 outline-none' />
    </ToastPrimitive.Provider>,
    document.body,
  );
};
